import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle2, ShieldCheck } from 'lucide-react';

import { addOab } from '@/api/client';
import {
  atualizarConfiguracao,
  type Configuracao,
  type EnderecoAdvogado,
  type TratamentoAdvogado,
} from '@/api/secretario';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import { UF_OPTIONS } from '@/constants/ufs';
import { useNotification } from '@/context/NotificationProvider';
import { useAuth } from '@/hooks/useAuth';
import { notifyMutationError } from '@/lib/errors';
import { toTitleCaseNome } from '@/lib/formatters';
import { queryKeys } from '@/lib/pwa/queryConfig';
import { comTratamento, tratamentoProvavel } from '@/lib/tratamento';

import { WizardActions } from './WizardActions';
import { useWizardSave } from './WizardSaveContext';
import { EscolhaDeTratamento } from './EscolhaDeTratamento';

interface StepProps {
  config: Configuracao | null;
  onNext: () => void;
  onBack: () => void;
  isFirst: boolean;
}

/** Passo 2 — dados do advogado: OAB, certificado (identidade), endereço e revisão. */
export function AdvogadoStep({ config, onNext, onBack, isFirst }: StepProps) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { showSuccess, showError } = useNotification();

  const [endereco, setEndereco] = useState<EnderecoAdvogado>(config?.advogado_endereco ?? {});
  // O tratamento não se pergunta quando dá para deduzir: o wizard mostra como o
  // advogado SERÁ chamado e oferece a troca. Só vira pergunta quando o prenome é
  // ambíguo (Alex, Ariel, Darci) — aí não há palpite honesto a dar.
  const palpite = tratamentoProvavel(user?.nome);
  const [tratamento, setTratamento] = useState<string>(config?.advogado_tratamento ?? palpite ?? '');
  // Nome na conversa: começa no do cadastro e só vira dado próprio quando difere
  // dele — "Marimar de Araújo" em vez de "Marimar Pereira de Araújo".
  const nomeCadastro = toTitleCaseNome((user?.nome ?? '').split(/\s+/).filter(Boolean).join(' '));
  const [nomeTratamento, setNomeTratamento] = useState(config?.advogado_nome_tratamento ?? nomeCadastro);
  const nomeTratamentoNormalizado = nomeTratamento.split(/\s+/).filter(Boolean).join(' ');
  const nomeTratamentoSalvo = nomeTratamentoNormalizado && nomeTratamentoNormalizado !== nomeCadastro
    ? nomeTratamentoNormalizado
    : null;
  const [numeroOab, setNumeroOab] = useState('');
  const [ufOab, setUfOab] = useState('');

  const invalidarConfig = () => queryClient.invalidateQueries({ queryKey: queryKeys.secretario.config });

  const salvarEndereco = useMutation({
    mutationFn: () =>
      atualizarConfiguracao({
        advogado_endereco: endereco,
        advogado_tratamento: tratamento === '' ? null : (tratamento as TratamentoAdvogado),
        advogado_nome_tratamento: nomeTratamentoSalvo,
      }),
    // Avançar fica no onPrimary — este save também roda no auto-save da navegação.
    onSuccess: () => {
      invalidarConfig();
      showSuccess('Dados salvos.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  // Auto-save ao navegar (nunca perder trabalho): endereço parcial também salva —
  // o backend aceita, e o gate de completude fica só no botão/prontidão.
  const registerFlush = useWizardSave();
  const { mutate: salvarEnderecoMutate, isPending: salvandoEndereco } = salvarEndereco;
  // Comparação campo a campo — JSON.stringify é sensível à ordem das chaves (a
  // local segue a ordem de digitação; a do backend é canônica) e deixa o dirty
  // preso em true, re-disparando saves redundantes a cada navegação.
  const CAMPOS_ENDERECO = ['logradouro', 'numero', 'complemento', 'bairro', 'cidade', 'uf', 'cep'] as const;
  const enderecoIgual = CAMPOS_ENDERECO.every(
    (campo) => (endereco[campo] ?? '') === (config?.advogado_endereco?.[campo] ?? ''),
  );
  // O palpite conta como sujo: ele precisa virar dado gravado, senão o backend
  // (que não deduz nada) continua sem saber como tratar o advogado.
  const dirty =
    config != null &&
    (!enderecoIgual ||
      tratamento !== (config.advogado_tratamento ?? '') ||
      nomeTratamentoSalvo !== (config.advogado_nome_tratamento ?? null));
  useEffect(() => {
    registerFlush('advogado', dirty && !salvandoEndereco ? () => salvarEnderecoMutate() : null);
    return () => registerFlush('advogado', null);
  }, [dirty, salvandoEndereco, salvarEnderecoMutate, registerFlush]);

  const salvarOab = useMutation({
    mutationFn: () => addOab({ numero: numeroOab.trim(), uf: ufOab.trim().toUpperCase() }),
    onSuccess: async () => {
      await refreshUser();
      invalidarConfig(); // a pendência de OAB por área é derivada no backend
      setNumeroOab('');
      setUfOab('');
      showSuccess('OAB cadastrada.');
    },
    onError: (error) => notifyMutationError(error, showError),
  });

  if (!config) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size={32} label="Carregando" />
      </div>
    );
  }

  const oab = user?.oabs?.find((o) => o.tipo === 'PRINCIPAL') ?? user?.oabs?.[0] ?? null;
  const semCertificado = config.prontidao.pendencias.some((p) => p.chave === 'certificado');

  return (
    <Card>
      <CardHeader>
        <CardTitle>Seus dados profissionais</CardTitle>
        <CardDescription>
          Sua identidade (OAB e certificado são necessários para ativar) e os dados usados no contrato e na
          procuração.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* OAB e certificado lado a lado: são os dois requisitos de identidade, e um
            embaixo do outro deixavam a tela comprida demais. */}
        <div className="grid gap-4 sm:grid-cols-2">
          {/* OAB ------------------------------------------------------------ */}
          <div className="space-y-2">
            <Label>
              OAB <span className="text-destructive">*</span>
            </Label>
            {oab ? (
              <div className="flex items-center gap-2 rounded-lg border p-3 text-sm">
                <span className="font-medium">
                  OAB/{oab.uf} {oab.numero}
                </span>
                {oab.verificada && <Badge variant="default">Verificada</Badge>}
              </div>
            ) : (
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[7rem] flex-1 space-y-1.5">
                  <Label htmlFor="oabNumero" className="text-xs text-muted-foreground">
                    Número
                  </Label>
                  <Input
                    id="oabNumero"
                    value={numeroOab}
                    onChange={(e) => setNumeroOab(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && numeroOab.trim() && ufOab.trim().length === 2 && !salvarOab.isPending) {
                        e.preventDefault();
                        salvarOab.mutate();
                      }
                    }}
                    placeholder="123456"
                    maxLength={32}
                  />
                </div>
                <div className="w-24 space-y-1.5">
                  <Label className="text-xs text-muted-foreground">UF</Label>
                  <Combobox
                    value={ufOab}
                    onValueChange={setUfOab}
                    options={UF_OPTIONS}
                    placeholder="UF"
                    searchPlaceholder="Buscar UF..."
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => salvarOab.mutate()}
                  disabled={salvarOab.isPending || !numeroOab.trim() || ufOab.trim().length !== 2}
                >
                  {salvarOab.isPending && <Spinner size={16} className="mr-2 text-inherit" />}
                  Salvar OAB
                </Button>
              </div>
            )}
          </div>

          {/* Certificado digital (prova de identidade) --------------------- */}
          <div className="space-y-2">
            <Label>Certificado digital</Label>
            {semCertificado ? (
              <Alert variant="warning">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="flex flex-col gap-2">
                  <span>
                    Necessário para ativar — é a prova de identidade do advogado (o CPF do certificado precisa
                    ser o seu).
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="self-start"
                    onClick={() => navigate('/app/certificados')}
                  >
                    <ShieldCheck className="mr-2 h-4 w-4" />
                    Cadastrar certificado
                  </Button>
                </AlertDescription>
              </Alert>
            ) : (
              <div className="flex items-center gap-2 rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3 text-sm">
                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                <span>Certificado cadastrado.</span>
              </div>
            )}
          </div>
        </div>

        {/* Como o Secretário chama você ---------------------------------- */}
        <div className="space-y-2">
          <Label htmlFor="nomeTratamento">Como o Secretário chama você</Label>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <EscolhaDeTratamento valor={tratamento} onEscolher={setTratamento} />
            <Input
              id="nomeTratamento"
              value={nomeTratamento}
              onChange={(e) => setNomeTratamento(e.target.value)}
              onBlur={() => !nomeTratamentoNormalizado && setNomeTratamento(nomeCadastro)}
              maxLength={120}
              className="sm:flex-1"
            />
          </div>
          <p className="text-xs text-muted-foreground">
            Na conversa com o cliente: “o seu caso está com{' '}
            {tratamento === 'DRA' ? 'a ' : tratamento === 'DR' ? 'o ' : ''}
            <strong>{comTratamento(nomeTratamentoNormalizado || nomeCadastro, tratamento as TratamentoAdvogado)}</strong>”.
            Contrato e procuração usam o nome completo do cadastro.
          </p>
        </div>

        {/* Endereço profissional ----------------------------------------- */}
        <div className="space-y-2">
          <Label>Endereço profissional</Label>
          <p className="text-xs text-muted-foreground">
            Opcional para ativar. Aparece no contrato de honorários e na procuração — sem ele, o Secretário faz
            só o pré-atendimento.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2">
              <Label htmlFor="endLogradouro">
                Logradouro
              </Label>
              <Input
                id="endLogradouro"
                value={endereco.logradouro ?? ''}
                onChange={(e) => setEndereco((p) => ({ ...p, logradouro: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="endNumero">Número</Label>
              <Input
                id="endNumero"
                value={endereco.numero ?? ''}
                onChange={(e) => setEndereco((p) => ({ ...p, numero: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="endComplemento">Complemento</Label>
              <Input
                id="endComplemento"
                value={endereco.complemento ?? ''}
                onChange={(e) => setEndereco((p) => ({ ...p, complemento: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="endBairro">Bairro</Label>
              <Input
                id="endBairro"
                value={endereco.bairro ?? ''}
                onChange={(e) => setEndereco((p) => ({ ...p, bairro: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="endCidade">
                Cidade
              </Label>
              <Input
                id="endCidade"
                value={endereco.cidade ?? ''}
                onChange={(e) => setEndereco((p) => ({ ...p, cidade: e.target.value }))}
              />
            </div>
            <div className="space-y-1.5">
              <Label>
                UF
              </Label>
              <Combobox
                value={endereco.uf ?? ''}
                onValueChange={(v) => setEndereco((p) => ({ ...p, uf: v }))}
                options={UF_OPTIONS}
                placeholder="UF"
                searchPlaceholder="Buscar UF..."
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="endCep">CEP</Label>
              <Input
                id="endCep"
                value={endereco.cep ?? ''}
                onChange={(e) => setEndereco((p) => ({ ...p, cep: e.target.value }))}
                maxLength={10}
              />
            </div>
          </div>
        </div>

        <WizardActions
          isFirst={isFirst}
          onBack={onBack}
          primaryLabel="Salvar e continuar"
          onPrimary={() => salvarEndereco.mutate(undefined, { onSuccess: onNext })}
          loading={salvarEndereco.isPending}
        />
      </CardContent>
    </Card>
  );
}
